import React, { useState, useMemo, useEffect, memo } from 'react';
import cx from 'clsx';

import { CHECKOUT_TEST_ID } from 'routes/Checkout/const';
import { useCloverPaymentIframeContext } from 'hooks/context/cloverPaymentIframe';
import { PaymentInfoErrors } from '../ReviewAndPayment';
import { PaymentInfo } from 'routes/Checkout/PaymentInfo';
import { Icon } from 'components/Icon';

import styles from './CloverPaymentSection.module.scss';

type CloverPaymentSectionProps = {
  // valid is true when the form is valid
  onValidityChange?: (valid: boolean) => void;
  children?: never;
};
export const CloverPaymentSection: React.FC<CloverPaymentSectionProps> = memo(({
  onValidityChange
}) => {
  const { cloverElementsInstance } = useCloverPaymentIframeContext();

  const [paymentInfoErrors, setPaymentInfoErrorsImpl] = useState<PaymentInfoErrors>();

  const [showPaymentErrors, setShowPaymentErrors] = useState(false);
  const [disableSubmitButton, setDisableSubmitButton] = useState(false);

  const inputErrors = useMemo((): (string | undefined)[] => {
    if (!paymentInfoErrors) return [];

    return Object.values(paymentInfoErrors.errors).filter(Boolean);
  }, [paymentInfoErrors]);

  useEffect(
    () => onValidityChange?.(inputErrors.length === 0 && (paymentInfoErrors?.isAllTouched || false) && !disableSubmitButton),
    [paymentInfoErrors, inputErrors, disableSubmitButton, onValidityChange]
  );

  // technically not possible at this point
  if (!cloverElementsInstance) return null;

  const setPaymentInfoErrors = (infoErrors?: PaymentInfoErrors ) => {
    setPaymentInfoErrorsImpl(prev => {
      if (!prev && infoErrors) return infoErrors;

      if (!prev || !infoErrors) return;

      const { errors, isAllTouched } = infoErrors;

      const keys = Object.keys(errors) as (keyof PaymentInfoErrors['errors'])[];

      const matches = keys.filter(key => errors[key] === prev.errors[key]);

      if (matches.length === keys.length && prev.isAllTouched === isAllTouched) return prev;

      return { errors, isAllTouched };
    });
  };

  const displayPaymentErrors = showPaymentErrors && !!inputErrors.length;

  return (
    <PaymentInfo
      cloverElementsInstance={cloverElementsInstance}
      toggleShowErrors={setShowPaymentErrors}
      onChange={setPaymentInfoErrors}
      toggleButtonDisableState={setDisableSubmitButton}
    >
      <div
        aria-live="polite"
        role="alert"
        className={cx(styles.paymentError, {
          [styles.paymentErrorHide] : !displayPaymentErrors
        })}
      >
        {displayPaymentErrors && <p data-testid={CHECKOUT_TEST_ID.error}>
          <Icon className={styles.icon} name="error" />
          {inputErrors[0]}
        </p>}
      </div>
    </PaymentInfo>
  );
});

